import React, { useEffect, useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { observer } from "mobx-react";

import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import { Loader } from "@components/loader/desktop";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { RegistrationFormData } from "@data-types/membership/registration";
import {
	PartnerRegistrationTabs,
	PartnerRegistrationTakeoverLimit,
} from "@features/membership/desktop/registration-types/partner-registration";
import {
	RegistrationError,
	RegistrationSuccess,
} from "@features/membership/desktop/registration-types/registration";
import {
	OnBoardingFooter,
	OnBoardingSlider,
} from "@features/membership/desktop/registration-types/shared";
import { useRootAppStore } from "@hooks";
import {
	PartnerRegistrationStoreContext,
	usePartnerRegistrationStore,
} from "@state/context/membership/registration-types/PartnerRegistrationStoreContext";
import { PartnerRegistrationStore } from "@state/stores/membership/partner-registration";
import { setTabTitle } from "@utils";

import { PartnerRegistrationAccountDetails } from "./PartnerRegistrationAccountDetails";
import { PartnerRegistrationContactDetails } from "./PartnerRegistrationContactDetails";

export const PartnerRegistrationPage = observer(
	function PartnerRegistrationPage() {
		const rootStore = useRootAppStore();

		const partnerRegistrationStore = useMemo(
			() => new PartnerRegistrationStore(rootStore),
			[]
		);

		const { t } = useTranslation("registration");

		const { onInitialize, onDispose } = partnerRegistrationStore;

		useEffect(() => {
			setTabTitle(t("REGISTRATION_TITLE"));

			const params = new URLSearchParams(window.location.search);

			if (params.get("shopId")) {
				onInitialize();
			} else {
				partnerRegistrationStore.isRegistrationSuccessful = false;
			}

			return () => onDispose();
		}, []);

		return (
			<PartnerRegistrationStoreContext.Provider
				value={partnerRegistrationStore}
			>
				<PartnerRegistrationPageContent />
			</PartnerRegistrationStoreContext.Provider>
		);
	}
);

type FormFillData = {
	firstName: string | null;
	lastName: string | null;
	dob: string | null;
	mobilePhone: string | null;
};

const PartnerRegistrationPageContent = observer(
	function PartnerRegistrationPageContent() {
		const methods = useForm<RegistrationFormData>({
			mode: "onChange",
			defaultValues: {
				languageId: "en-US",
			},
		});
		const { handleSubmit, setValue } = methods;

		const { t } = useTranslation("registration");

		const {
			isRegistrationSuccessful,
			isLoading,
			currentStep,
			register,
			takeoverTempInfoId,
		} = usePartnerRegistrationStore();

		const params = new URLSearchParams(window.location.search);
		const shopId = params.get("shopId") as string;

		useEffect(() => {
			const formData: FormFillData = {
				firstName: null,
				lastName: null,
				dob: null,
				mobilePhone: null,
			};
			if (params.get("firstname")) {
				formData.firstName = params.get("firstname");
			}
			if (params.get("lastname")) {
				formData.lastName = params.get("lastname");
			}
			if (params.get("dob")) {
				formData.dob = params.get("dob");
			}
			if (params.get("phonenumber")) {
				//phone number needs to contain +,
				//if we receive + symbol in params it is recognized as whitespace
				formData.mobilePhone = "+" + params.get("phonenumber");
			}

			for (const key of Object.keys(formData) as Array<
				keyof FormFillData
			>) {
				const value = formData[key];
				if (value != undefined) {
					setValue(key, value as keyof RegistrationFormData);
				}
			}
		}, []);

		if (isRegistrationSuccessful === true) {
			return <RegistrationSuccess />;
		}

		if (isRegistrationSuccessful === false) {
			return <RegistrationError />;
		}

		if (takeoverTempInfoId) {
			return <PartnerRegistrationTakeoverLimit />;
		}

		return (
			<DesktopLayout
				headerType={HeaderType.EMPTY}
				mainClassName="at-PartnerRegistration l--main l--main--grow"
			>
				<Loader isLoading={isLoading} />

				<section className="l--content l--content--full">
					<div className="l--content__main u-padd--reset">
						<div className="container container--med">
							<div className="card--onboarding card--shadowy">
								<OnBoardingSlider />
								<FormProvider {...methods}>
									<form
										className="card--onboarding__form"
										onSubmit={handleSubmit(
											(formData: RegistrationFormData) =>
												register(formData, shopId)
										)}
									>
										<div className="card--onboarding__header">
											<div className="card--onboarding__title">
												{currentStep === 1
													? t("ACCOUNT_DETAILS_TAB")
													: t("CONTACT_DETAILS_TAB")}
											</div>
											<PartnerRegistrationTabs />
										</div>

										<RegistrationSteps />
									</form>
								</FormProvider>
							</div>

							<OnBoardingFooter isRegistration />
						</div>
					</div>
				</section>
			</DesktopLayout>
		);
	}
);

const RegistrationSteps = observer(function RegistrationSteps() {
	const { currentStep } = usePartnerRegistrationStore();

	switch (currentStep) {
		case 1:
			return <PartnerRegistrationAccountDetails />;

		case 2:
			return <PartnerRegistrationContactDetails />;

		default:
			return null;
	}
});
